import React from 'react';

import { ErrorReporter } from 'js/src/libs/error-reporter';

const HUBSPOT_EMBED_SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js';

let embedScriptPromise: Promise<void> | null = null;
let formInstanceCount = 0;

function loadHubSpotEmbedScript(): Promise<void> {
    if (embedScriptPromise) {
        return embedScriptPromise;
    }
    embedScriptPromise = new Promise<void>((resolve, reject) => {
        if (window.hbspt) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = HUBSPOT_EMBED_SCRIPT_SRC;
        script.charset = 'utf-8';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            // allow a later mount to retry the load
            embedScriptPromise = null;
            reject(new Error('Failed to load the HubSpot forms embed script'));
        };
        document.body.appendChild(script);
    });
    return embedScriptPromise;
}

export type HubSpotFormProps = {
    portalId: string;
    formId: string;
    region?: string;
    className?: string;
    onFormSubmitted?: VoidFunction;
};
const HubSpotForm: React.FC<HubSpotFormProps> = React.memo(function HubSpotForm({
    portalId,
    formId,
    region,
    className,
    onFormSubmitted,
}) {
    const [instanceId] = React.useState(() => `${(formInstanceCount += 1)}`);
    const targetId = `hubspot-form-${instanceId}`;
    const onFormSubmittedRef = React.useRef(onFormSubmitted);
    onFormSubmittedRef.current = onFormSubmitted;

    React.useEffect(() => {
        let isMounted = true;
        loadHubSpotEmbedScript()
            .then(() => {
                if (!isMounted || !window.hbspt) {
                    return;
                }
                window.hbspt.forms.create({
                    portalId,
                    formId,
                    region,
                    target: `#${targetId}`,
                    formInstanceId: instanceId,
                    onFormSubmitted: () => onFormSubmittedRef.current?.(),
                });
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
            });

        return () => {
            isMounted = false;
            const target = document.getElementById(targetId);
            if (target) {
                target.innerHTML = '';
            }
        };
    }, [formId, instanceId, portalId, region, targetId]);

    return <div id={targetId} className={className} data-testid="hubspot-form" />;
});

export default HubSpotForm;
